import { FeatureFlag } from '@ps/web-core-enums';
import type { EventId } from '@ps/web-sports-api-types';
import { useEffect, useMemo } from 'react';

import useFeature from '@swf/core/hooks/useFeature';
import { dispatch } from '@swf/core/store';
import { useFootballQuery, useTennisQuery } from '@swf/generated/graphql/scoreboards/hooks';
import type { FootballQuery, TennisQuery } from '@swf/generated/graphql/scoreboards/operations';
import type {
  FootballFixture,
  Maybe,
  TennisMatch
} from '@swf/generated/graphql/scoreboards/schemas';

import { EventTypeId } from '../../types/eventType';
import type { EventTypesWithEventIds, ScoreboardData } from '../../types/scoreboards';

const POLLING_INTERVAL = 5000;

interface IsDataProps {
  football?: unknown;
  tennis?: unknown;
}

const isFootballData = (data?: IsDataProps): data is FootballQuery => !!data?.football;
const isTennisData = (data?: IsDataProps): data is TennisQuery => !!data?.tennis;

interface TransformScoreboardDataProps {
  eventTypeId: EventTypeId;
  ids: string[];
  queryResultData?: FootballQuery | TennisQuery;
}

const transformScoreboardData = ({
  eventTypeId,
  ids,
  queryResultData
}: TransformScoreboardDataProps) => {
  let scoreboardDataToTransform: (Maybe<FootballFixture> | Maybe<TennisMatch>)[] | undefined;

  if (isFootballData(queryResultData)) {
    scoreboardDataToTransform = queryResultData.football?.fixture as Maybe<FootballFixture>[];
  } else if (isTennisData(queryResultData)) {
    scoreboardDataToTransform = queryResultData.tennis?.match as Maybe<TennisMatch>[];
  }

  if (!scoreboardDataToTransform) {
    return {};
  }

  return ids.reduce<Record<EventId, ScoreboardData>>((scoreboardData, id) => {
    const data = scoreboardDataToTransform?.find((data) => data?.id === id);

    const liveEventInfo = {
      [id]: {
        eventTypeId,
        data,
        hasError: !data
      } as ScoreboardData
    };

    return {
      ...scoreboardData,
      ...liveEventInfo
    };
  }, {});
};

const useScoreboardPolling = (eventTypesWithEventIds: Partial<EventTypesWithEventIds>): void => {
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPScoreboards);

  const footballIds = useMemo(
    () => eventTypesWithEventIds[EventTypeId.SOCCER]?.map((id) => id.toString()) ?? [],
    [eventTypesWithEventIds]
  );
  const tennisIds = useMemo(
    () => eventTypesWithEventIds[EventTypeId.TENNIS]?.map((id) => id.toString()) ?? [],
    [eventTypesWithEventIds]
  );

  useEffect(
    () =>
      function cleanup() {
        dispatch.scoreboards.scoreboardsCleared();
      },
    []
  );

  const footballQueryResult = useFootballQuery({
    variables: {
      eventIds: footballIds
    },
    skip: !footballIds.length || !isScoreboardsEnabled,
    pollInterval: POLLING_INTERVAL,
    errorPolicy: 'all'
  });

  const tennisQueryResult = useTennisQuery({
    variables: {
      eventIds: tennisIds
    },
    skip: !tennisIds.length || !isScoreboardsEnabled,
    pollInterval: POLLING_INTERVAL,
    errorPolicy: 'all'
  });

  useEffect(() => {
    if (footballQueryResult.error?.networkError || tennisQueryResult.error?.networkError) {
      dispatch.scoreboards.scoreboardsNetworkError();
      return;
    }

    const scoreboardState = {
      ...transformScoreboardData({
        eventTypeId: EventTypeId.SOCCER,
        ids: footballIds,
        queryResultData: footballQueryResult.data ?? footballQueryResult.previousData
      }),
      ...transformScoreboardData({
        eventTypeId: EventTypeId.TENNIS,
        ids: tennisIds,
        queryResultData: tennisQueryResult.data ?? tennisQueryResult.previousData
      })
    };

    dispatch.scoreboards.scoreboardsUpdated({
      scoreboards: scoreboardState
    });
  }, [
    footballIds,
    footballQueryResult.data,
    footballQueryResult.error,
    footballQueryResult.previousData,
    tennisIds,
    tennisQueryResult.data,
    tennisQueryResult.error,
    tennisQueryResult.previousData
  ]);
};

export default useScoreboardPolling;
